/**
 * @file `GET /families/{familyId}/members` のクライアント (学習者の候補を得る)。
 * @what: family の member 一覧から、学習者候補 (active な child と養育者) を取り出す。
 * @why: いちごスタディの plan は学習者 (person) 単位。伴走対象期の学習者は role child だが、
 *   養育者自身の学習 (資格・教養・受験研究) も同じ器に載せる (epic_23 §10)。
 */

import { apiFetch } from "@/shared/api/client";

export interface FamilyMember {
  person_id: string;
  display_name?: string;
  life_status?: string;
  role: string;
  status: string;
  nickname?: string;
}

interface FamilyMemberListResponse {
  items: FamilyMember[];
}

export async function fetchFamilyMembers(
  familyId: string,
  signal?: AbortSignal,
): Promise<FamilyMember[]> {
  const res = await apiFetch<FamilyMemberListResponse>(
    `/families/${encodeURIComponent(familyId)}/members`,
    signal ? { signal } : {},
  );
  return res.items;
}

/**
 * おとなの学習者になれる role (養育者)。
 *
 * @why: 祖父母・親戚は学習者候補に出さない。計画層は養育者のもの (epic_23 レンズ) で、
 *   候補を広げると誰の学習の面か分からなくなる。
 */
const ADULT_LEARNER_ROLES = new Set(["parent", "guardian", "caregiver"]);

/** おとな (養育者) の学習者かどうか。子ども向けの面 (計画・領域・よむ) の出し分けに使う。 */
export function isAdultLearner(m: FamilyMember): boolean {
  return ADULT_LEARNER_ROLES.has(m.role);
}

/** 学習者候補: active な child と養育者。お子さんが先で、各並びは API の順序を保つ。 */
export function learnersOf(members: FamilyMember[]): FamilyMember[] {
  const active = members.filter((m) => m.status === "active");
  return [...active.filter((m) => m.role === "child"), ...active.filter(isAdultLearner)];
}

export function memberLabel(m: FamilyMember): string {
  if (m.nickname && m.nickname.length > 0) return m.nickname;
  if (m.display_name && m.display_name.length > 0) return m.display_name;
  return m.role === "child"
    ? `お子さん ${m.person_id.slice(0, 8)}`
    : `ご家族 ${m.person_id.slice(0, 8)}`;
}
